import { Injectable } from '@angular/core';
import { MomentHelpers } from '@seekda/bm-components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ObjectHelpers } from '@bmng/helpers/object-helpers';
import { SubscriptionReportPayload } from '../../../pages/generic/reports/common/types/report-type.type';
import { SubscriptionReportDetails } from '../reports/interfaces/reports-subscription.interface';
import { ReportsService } from '../reports/reports.service';

import {
    BookingSummaryGroupingColumn,
    BookingSummaryParams,
    BookingSummaryServiceInterface,
} from './interfaces/booking-summary-service.interface';
import { BookingSummary } from './interfaces/booking-summary.interface';

@Injectable({
    providedIn: 'root',
})
export class BookingDataService implements BookingSummaryServiceInterface {
    constructor(
        private reportsService: ReportsService,
    ) { }

    getBookingSummary(params: BookingSummaryParams, type: BookingSummaryGroupingColumn): Observable<BookingSummary[]> {
        if (type === 'SERVICE_CODES') {
            return this.getServicesReport(params);
        }

        return this.reportsService.getOnDemandReport(
            params.propertyCode,
            'hotel',
            this.getReportPayloadFrom(params, type),
        ).pipe(
            map(report => {
                const results = report.result || [];

                return results.map((pkgResult: SubscriptionReportDetails) => {
                    let code = '';

                    switch (type) {
                        case 'RATE_CODES':
                            code = pkgResult.rateCodes;
                            break;

                        case 'PACKAGE_CODES':
                            code = pkgResult.packageCodes;
                            break;

                        default:
                            ObjectHelpers.assertNever(type);
                    }

                    return {
                        code,
                        revenue: pkgResult.total,
                        bookings: pkgResult.bookings,
                        type,
                    };
                });
            }),
        );
    }

    private getServicesReport(params: BookingSummaryParams): Observable<BookingSummary[]> {
        return this.reportsService.getOnDemandReport(params.propertyCode, 'hotel', this.getServiceReportPayload(params)).pipe(
            map(report => {
                const results = report.result || [];

                return results.map(service => ({
                    code: service.serviceCode,
                    revenue: service.totalServiceAmount,
                    bookings: service.totalServices,
                    type: 'SERVICE_CODES',
                }));
            }),
        );
    }

    private getReportPayloadFrom(params: BookingSummaryParams, type: BookingSummaryGroupingColumn): SubscriptionReportPayload {
        const payload: SubscriptionReportPayload = {
            subscription: {
                id: null,
                enabled: false,
                name: null,
                recurrence: null,
                dataWindow: null,
                dataWindowStart: null,
                dataWindowEnd: null,
                firstDayOfWeek: null,
                type: 'RESERVATION',
                variant: 'CUSTOM',
                recipients: null,
                createdBy: null,
                modifiedBy: null,
                created: null,
                modified: null,
                scheduledDateTime: null,
                languageCode: null,
                custom: {
                    grouping: {
                        primary: type,
                        secondary: null,
                        tertiary: null,
                    },
                    columns: [
                        {
                            id: type,
                            enabled: true,
                        },
                        {
                            id: 'BOOKINGS',
                            enabled: true,
                        },
                        {
                            id: 'TOTAL',
                            enabled: true,
                        },
                    ],
                    filters: !!params.filters ? params.filters : {},
                    kpis: null,
                    summary: null,
                },
                timezone: MomentHelpers.getTimezone(),
            },
            size: null,
            start: null,
        };

        if (!!params.currencyCode) {
            payload.subscription.currency = params.currencyCode;
        }

        if (!params.startDate) {
            payload.subscription.dataWindow = 'YEAR_UP_TO_DATE';
        } else {
            payload.subscription.dataWindowStart = params.startDate;
            payload.subscription.dataWindowEnd = params.endDate;
        }

        return payload;
    }

    private getServiceReportPayload(params: BookingSummaryParams): SubscriptionReportPayload {
        const payload: SubscriptionReportPayload = {
            subscription: {
                id: null,
                enabled: false,
                name: 'Custom Report',
                type: 'SERVICE',
                variant: 'CUSTOM',
                recurrence: null,
                recipients: [],
                dataWindow: null,
                dataWindowStart: null,
                dataWindowEnd: null,
                firstDayOfWeek: null,
                created: '',
                modified: '',
                createdBy: null,
                modifiedBy: null,
                scheduledDateTime: null,
                custom: {
                    grouping: {
                        primary: 'SERVICE_CODE',
                        secondary: '',
                        tertiary: '',
                    },
                    columns: [
                        {
                            id: 'SERVICE_CODE',
                            enabled: true,
                        },
                        {
                            id: 'TOTAL_SERVICE_AMOUNT',
                            enabled: true,
                        },
                        {
                            id: 'TOTAL_SERVICES',
                            enabled: true,
                        },
                    ],
                    filters: (params.filters ?? {}),
                    kpis: [],
                },
            },
            sort: 'SERVICE_CODE',
            sortType: 'DESC',
            start: null,
            size: null,
            q: null,
            filters: {},
        };

        if (!!params.currencyCode) {
            payload.subscription.currency = params.currencyCode;
        }

        if (!params.startDate) {
            payload.subscription.dataWindow = 'YEAR_UP_TO_DATE';
        } else {
            payload.subscription.dataWindowStart = params.startDate;
            payload.subscription.dataWindowEnd = params.endDate;
        }

        return payload;
    }
}
